import * as React from 'react';

import {FlashBanner} from 'shells/flash-banner/flash-banner';
// @ts-expect-error - imported from .js/.jsx file
import {LoadingIcon} from 'shells/icon/svg/loading-icon';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {Link} from 'shells/link/link';

import type {StagesetsForPipelineList_stagesets as Stageset} from 'nutshell-graphql-types';

import {BannerCueCards} from '../../glossary/layout/banner-cue-cards';

import {DraggablePipelineList} from './draggable-pipeline-list';
import {PipelineList} from './pipeline-list';
import {PipelineListItem} from './pipeline-list-item';

import styles from './sales-automation-pipeline-list.module.css';

const ACTIVE_STATUS = 'ACTIVE';
const DRAFT_STATUS = 'DRAFT';
const RETIRED_STATUS = 'RETIRED';

type Props = {
    activeTab: string;
    stagesets: Stageset[];
    isLoading: boolean;
    errorMessage: string | null | undefined;
};

export const SalesAutomationPipelineList = (props: Props) => {
    const filteredStagesets = React.useMemo(() => {
        if (!NutClientConfig.hasDraftPipelinesFF) {
            return props.stagesets;
        }

        return props.stagesets.filter((stageset) => {
            switch (props.activeTab) {
                case 'ACTIVE':
                    return stageset.status === ACTIVE_STATUS && !stageset.isArchived;
                case 'DRAFT':
                    return (
                        (stageset.status === DRAFT_STATUS || stageset.status === RETIRED_STATUS) &&
                        !stageset.isArchived
                    );
                case 'ARCHIVED':
                    return stageset.isArchived;
                default:
                    return stageset;
            }
        });
    }, [props.stagesets, props.activeTab]);

    if (props.isLoading) {
        return (
            <div className={styles.container}>
                <LoadingIcon />
            </div>
        );
    }

    if (props.errorMessage) {
        return (
            <div className={styles.container}>
                <img
                    src='/include/images/dashboard/list-summary-error_2x.png'
                    className={styles['error-image']}
                />
                <div className={styles['error-text']}>
                    We’re sorry! There was a problem loading your pipelines and stages.
                </div>
            </div>
        );
    }

    const defaultPipeline = filteredStagesets.find((stageset) => {
        return stageset.default;
    });

    const nonDefaultAccessiblePipelines = filteredStagesets.filter((stageset) => {
        return !stageset.default && !stageset.isArchived && stageset.canAccess;
    });

    const activePipelines = nonDefaultAccessiblePipelines.filter((stageset) => {
        return stageset.status === ACTIVE_STATUS;
    });

    const draftPipelines = nonDefaultAccessiblePipelines.filter((stageset) => {
        return stageset.status === DRAFT_STATUS || stageset.status === RETIRED_STATUS;
    });

    const inaccessiblePipelines = filteredStagesets.filter((stageset) => {
        return !stageset.default && !stageset.isArchived && !stageset.canAccess;
    });

    const archivedPipelines = filteredStagesets.filter((stageset) => {
        return stageset.isArchived;
    });

    const pipelineIdentifier = NutClientConfig.hasDraftPipelinesFF
        ? 'active pipelines'
        : 'pipelines';

    return (
        <div className={styles['pipelines-container']}>
            <StackLegacy spacing={16}>
                {inaccessiblePipelines.length && (
                    <FlashBanner
                        type='warning'
                        leftAlignContent={true}
                        animate={false}
                        hasBorder={true}
                    >
                        <div className='full-width justify-sb flex align-center'>
                            {inaccessiblePipelines.length === 1
                                ? `1 of your ${pipelineIdentifier} is`
                                : `${inaccessiblePipelines.length} of your ${pipelineIdentifier} are`}{' '}
                            not accessible on your current plan.
                            <Link variant='primary' href='/setup/billing/sales' newTab={true}>
                                View billing
                            </Link>
                        </div>
                    </FlashBanner>
                )}
                {defaultPipeline && (
                    <PipelineListItem stageset={defaultPipeline} shouldPreventDragging={true} />
                )}
                {nonDefaultAccessiblePipelines.length || archivedPipelines.length ? (
                    <div className={styles['pipeline-list']}>
                        <DraggablePipelineList stagesets={activePipelines} />
                        <PipelineList stagesets={draftPipelines} />
                        <PipelineList stagesets={archivedPipelines} />
                    </div>
                ) : undefined}
                <BannerCueCards />
            </StackLegacy>
        </div>
    );
};
